/** @format */

/**
 * Span 原始日志抽屉：以 JSON 只读展示当前 Span，由父组件控制 visible / onClose。
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Controlled as CodeMirror } from 'react-codemirror2'
import type { Editor } from 'codemirror'
import styled from 'styled-components'
import { _$, Drawer } from '@king-design/react'
import type { TraceAnalysisDetailSpan } from '../typescript'

import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/material.css'
import 'codemirror/mode/javascript/javascript'

const EDITOR_HEIGHT = 'calc(100vh - 160px)'

export interface TraceSpanLogProps {
  className?: string
  visible: boolean
  span: TraceAnalysisDetailSpan | null
  onClose: () => void
}

/** 将 Span 格式化为缩进 JSON 字符串 */
function formatSpanJson(span: TraceAnalysisDetailSpan | null): string {
  if (!span) {
    return ''
  }
  try {
    return JSON.stringify(span, null, 2)
  } catch {
    return ''
  }
}

const TraceSpanLog: React.FC<TraceSpanLogProps> = ({ className, visible, onClose, span }) => {
  const editorRef = useRef<Editor | null>(null)
  const jsonValue = useMemo(() => formatSpanJson(span), [span])

  const refreshEditor = useCallback(() => {
    const editor = editorRef.current
    if (!editor) {
      return
    }
    editor.setSize('100%', EDITOR_HEIGHT)
    editor.refresh()
  }, [])

  /** 抽屉展开动画结束后再 refresh，避免初始宽高为 0 导致空白 */
  useEffect(() => {
    if (!visible || !span) {
      return undefined
    }
    const timer = window.setTimeout(() => {
      refreshEditor()
    }, 150)
    return () => window.clearTimeout(timer)
  }, [visible, span, jsonValue, refreshEditor])

  const handleBeforeChange = useCallback(() => {
    // 原始日志只读展示，不接收编辑内容。
  }, [])

  const handleEditorDidMount = useCallback(
    (editor: Editor) => {
      editorRef.current = editor
      window.setTimeout(() => {
        refreshEditor()
      }, 150)
    },
    [refreshEditor]
  )

  return (
    <div className={className}>
      <Drawer
        value={visible}
        width={900}
        title={_$('原始日志')}
        mode="destroy"
        hideClose
        container={() => document.getElementById('trace_span_detail_drawer')}
        onChangeValue={show => {
          if (!show) {
            onClose()
          }
        }}
      >
        <div className="trace-span-log-body">
          {visible && span ? (
            <CodeMirror
              value={jsonValue}
              onBeforeChange={handleBeforeChange}
              editorDidMount={handleEditorDidMount}
              options={{
                mode: 'application/json',
                theme: 'material',
                lineNumbers: true,
                lineWrapping: true,
                readOnly: true,
                foldGutter: true,
                gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
                matchBrackets: true,
                indentUnit: 2,
                tabSize: 2,
                indentWithTabs: false
              }}
            />
          ) : (
            <p className="trace-span-log-empty">{_$('暂无 Span 数据')}</p>
          )}
        </div>
      </Drawer>
    </div>
  )
}

export default styled(TraceSpanLog)`
  .trace-span-log-body {
    min-height: 320px;

    .CodeMirror {
      height: calc(100vh - 160px);
      border-radius: 4px;
      border: 1px solid #f0f0f0;
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 14px;
      line-height: 1.5;
    }

    .CodeMirror-linenumber {
      color: #999;
    }
  }

  .trace-span-log-empty {
    margin: 48px 0;
    text-align: center;
    color: rgba(0, 0, 0, 0.45);
    font-size: 13px;
  }
`
